import { useTheme as useThemeReact } from "@bpipartners/webui";
import { createTheme } from "@bpipartners/webui";
import { dimensions } from "./dimensions";
import localFont from "next/font/local";
import { CustomAppPalette } from "./theme.model";
import { getAppColors, defaultAppPalette } from "./patron.colors";

// Font files can be colocated inside of `app`
// const Dongle = localFont({
//   src: "./fonts/Dongle-Regular.ttf",
//   display: "swap",
// });

const Ubuntu = localFont({
  src: "./fonts/Ubuntu-Regular.ttf",
  display: "swap",
});

export const getTheme = (customPalette?: CustomAppPalette) => {
  const customCombinedPalette = { ...defaultAppPalette, ...customPalette };

  const appColors = getAppColors(customCombinedPalette);

  // any because materialTheme typing expecting everything
  // eslint-disable-next-line

  const materialTheme = createTheme({
    spacing: 2,
    components: {
      MuiSelect: {
        defaultProps: { variant: "filled" },
        styleOverrides: {
          root: {
            "& .MuiMenuItem-root.Mui-selected": {
              backgroundColor: customCombinedPalette.secondary[95],
            },
          },
        },
      },
      MuiFormControl: {
        defaultProps: { variant: "filled", size: "small" },
        styleOverrides: { root: { marginLeft: 0, marginRight: 0 } },
      },
      // MuiFormControlLabel: {
      //   styleOverrides: { root: { marginLeft: 0, marginRight: 0 } },
      // },
      MuiTextField: {
        defaultProps: { variant: "filled", size: "small" },
      },
      MuiDatePicker: {
        defaultProps: { slotProps: { textField: { variant: "filled", size: "small" } } },
      },
      MuiDateRangePicker: {
        defaultProps: { slotProps: { textField: { variant: "filled", size: "small" } } },
      },
      MuiDateTimePicker: {
        defaultProps: { slotProps: { textField: { variant: "filled", size: "small" } } },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: appColors.surfaceContainerLowest,
          },
        },
      },
      MuiPaper: {
        defaultProps: {
          variant: "outlined",
        },
        styleOverrides: {
          root: {
            borderRadius: 12,
            backgroundColor: appColors.surfaceContainerLowest,
            // border: `0px solid ${appColors.surfaceContainerLowest}`,
          },
        },
      },

      MuiStack: {
        defaultProps: {
          spacing: 4,
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            borderRadius: 0,
          },
        },
      },
      MuiButton: {
        defaultProps: {
          size: "medium",
        },
        styleOverrides: {
          root: {
            borderRadius: 24,
            height: 36,
            textTransform: "none",
          },
          outlined: {
            border: `1px solid ${appColors.surfaceContainerHigh}`,
          },
        },
      },
      MuiDataGrid: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: customCombinedPalette.primary[95],
              // borderRadius: 0,
            },
            "& .MuiDataGrid-pinnedColumnHeaders": {
              backgroundColor: customCombinedPalette.primary[95],
              // borderRadius: 0,
            },
            "& .MuiDataGrid-pinnedColumns": {
              backgroundColor: appColors.surfaceContainerHigh,
              // borderRadius: 0,
            },
            "& .MuiDataGrid-headerFilterRow": {
              backgroundColor: customCombinedPalette.primary[95],
              // borderRadius: 0,
              // border: "none",
            },
            "& .MuiDataGrid-footerContainer": {
              // backgroundColor: customCombinedPalette.primary[95],
              minHeight: 40,
            },
            "& .MuiDataGrid-row.Mui-selected ": {
              backgroundColor: customCombinedPalette.secondary[95],
              cursor: "pointer",
            },
            "& .MuiDataGrid-row.Mui-selected:hover ": {
              backgroundColor: customCombinedPalette.secondary[90],
              cursor: "pointer",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: customCombinedPalette.secondary[90],
              cursor: "pointer",
            },
            "& .MuiDataGrid-cell:focus": {
              outline: "none",
            },
            "& .MuiDataGrid-cell": {
              alignContent: "center",
            },
          },
        },
      },
    },
    palette: {
      DataGrid: {
        headerBg: customCombinedPalette.primary[95],
        pinnedBg: customCombinedPalette.primary[95],
      },
      // type: "light",
      background: {
        default: customCombinedPalette.neutral[100],
        paper: appColors.surfaceContainerLowest,
      },
      primary: {
        light: customCombinedPalette.primary[80],
        main: customCombinedPalette.primary[60],
        dark: customCombinedPalette.primary[30],
        contrastText: appColors.onPrimary,
      },
      secondary: {
        light: customCombinedPalette.secondary[60],
        main: appColors.secondary,
        dark: customCombinedPalette.secondary[30],
        contrastText: appColors.onSecondary,
      },
    },
    typography: {
      fontFamily: Ubuntu.style.fontFamily,
      fontSize: 14,
    },
  });
  return {
    ...materialTheme,
    // Override material default: show header above drawer (sidebar)
    zIndex: {
      ...materialTheme.zIndex,
      appBar: materialTheme.zIndex.drawer,
      drawer: materialTheme.zIndex.appBar,
    },
    appColors: appColors,
    appPalette: customCombinedPalette,
    dimensions,
  };
};

export type Theme = ReturnType<typeof getTheme>;

export const useTheme = () => useThemeReact() as Theme;
